import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  ClientInsuranceHttpService,
  IPatientInsurance
} from '@sondermind/data-access/client-insurance';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { parseErrorCodes, IAPIErrorCodes } from '@sondermind/utilities/models-http';

interface ProBonoFormValue {
  maxRedemptions: number;
}

@Component({
  selector: 'member-client-add-pro-bono-modal',
  templateUrl: './pro-bono-modal.component.html',
  styleUrls: ['./pro-bono-modal.component.scss'],
  standalone: false
})
export class ProBonoModalComponent implements OnInit {
  saving = false;
  loaded = false;

  form: UntypedFormGroup;
  errors: IAPIErrorCodes = null;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;

  constructor(
    private clientInsuranceHttp: ClientInsuranceHttpService,
    private builder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private irisModalRef: IrisModalRef<
    ProBonoModalComponent,
    { patientId: number; currentCount: number; },
    IPatientInsurance
    >,
  ) { }

  ngOnInit() {
    this.form = this.builder.group({
      maxRedemptions: this.builder.control(this.currentCount, [Validators.required, Validators.min(this.currentCount)])
    });
  }

  get patientId() {
    return this.irisModalRef?.config?.data?.patientId;
  }

  get currentCount() {
    return this.irisModalRef?.config?.data?.currentCount;
  }

  get formValue(): ProBonoFormValue {
    return this.form.value as ProBonoFormValue;
  }

  submit(): void {
    if (!this.form.valid) { return }

    const value: ProBonoFormValue = this.formValue;
    const addRedemptions = value.maxRedemptions - this.currentCount;

    // if nothing added, this is effectively a no-op
    if (addRedemptions === 0) {
      this.irisModalRef.close(null);
      return;
    }

    this.saving = true;
    this.errors = null;

    this.clientInsuranceHttp
      .addProBono(this.patientId, addRedemptions, { error: { rawErrors: true } })
      .subscribe({
        next: (svr) => {
          this.saving = false;
          this.irisModalRef.close(svr);
        },
        error: (err) => {
          this.saving = false;

          if (err.status === 400) {
            this.errors = parseErrorCodes(err.fields ?? err);
          } else {
            this.errors = { base: { serverError: true } };
            this.snackBar.open('Error while gifting sessions, please try again.');
          }

          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }
}
